import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TeamMetrics {
  team_id: string;
  league_id: string | null;
  venue_filter: string;
  games_played: number;
  goals_scored: number;
  total_shots: number;
  shots_on_target: number;
  corners: number;
  shots_per_goal: number | null;
  sot_per_goal: number | null;
  corners_per_goal: number | null;
}

interface UseTeamSeasonMetricsResult {
  homeMetrics: TeamMetrics | null;
  awayMetrics: TeamMetrics | null;
  isLoading: boolean;
}

/**
 * Fetches pre-computed season metrics for both teams.
 * Uses venue-specific data (home metrics for home team, away for away team).
 * Falls back to 'all' if venue-specific sample is too small.
 */
export function useTeamSeasonMetrics(
  homeTeamId: string | undefined,
  awayTeamId: string | undefined,
  season = '2025'
): UseTeamSeasonMetricsResult {
  const { data, isLoading } = useQuery({
    queryKey: ['team-season-metrics', homeTeamId, awayTeamId, season],
    queryFn: async () => {
      if (!homeTeamId || !awayTeamId) return null;
      
      const teamIds = [homeTeamId, awayTeamId];
      const { data: rows, error } = await supabase
        .from('team_season_metrics' as any)
        .select('*')
        .in('team_id', teamIds)
        .eq('season', season)
        .in('venue_filter', ['home', 'away', 'all']);
      
      if (error || !rows) return null;
      return rows as unknown as TeamMetrics[];
    },
    enabled: !!homeTeamId && !!awayTeamId,
    staleTime: 30 * 60 * 1000, // 30 min
    gcTime: 60 * 60 * 1000,
  });

  if (!data || isLoading) {
    return { homeMetrics: null, awayMetrics: null, isLoading };
  }

  // Pick best metrics for home team (prefer venue='home', fallback to 'all')
  const homeVenue = data.find(r => r.team_id === homeTeamId && r.venue_filter === 'home');
  const homeAll = data.find(r => r.team_id === homeTeamId && r.venue_filter === 'all');
  const homeMetrics = (homeVenue && homeVenue.games_played >= 5 && homeVenue.goals_scored >= 3)
    ? homeVenue
    : homeAll ?? homeVenue ?? null;

  // Pick best metrics for away team (prefer venue='away', fallback to 'all')
  const awayVenue = data.find(r => r.team_id === awayTeamId && r.venue_filter === 'away');
  const awayAll = data.find(r => r.team_id === awayTeamId && r.venue_filter === 'all');
  const awayMetrics = (awayVenue && awayVenue.games_played >= 5 && awayVenue.goals_scored >= 3)
    ? awayVenue
    : awayAll ?? awayVenue ?? null;

  return { homeMetrics, awayMetrics, isLoading };
}
